import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  SHAPE_OPTIONS,
  COLOR_OPTIONS,
  CLARITY_OPTIONS,
} from "@/lib/diamondSpecs";

export const Route = createFileRoute("/quiz")({
  head: () => ({
    meta: [
      { title: "Find Your Stone, HOWDEN" },
      {
        name: "description",
        content:
          "A short guided quiz to narrow the collection to the diamonds best suited to you.",
      },
      { property: "og:title", content: "Find Your Stone, HOWDEN" },
      {
        property: "og:description",
        content:
          "A short guided quiz to narrow the collection to the diamonds best suited to you.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/quiz" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/quiz" }],
  }),
  component: QuizPage,
});

type Style = "classic" | "modern" | "vintage" | "statement";
type Priority = "size" | "sparkle" | "purity" | "balance";
type Budget = "under5k" | "5to10k" | "10to20k" | "over20k" | "over5k";
type Size = "petite" | "classic" | "bold" | "statement";

interface Answers {
  style: Style | null;
  priority: Priority | null;
  budget: Budget | null;
  size: Size | null;
  shape: string | null;
}

const SHAPE_BY_STYLE: Record<Style, string[]> = {
  classic: ["Round", "Oval", "Princess"],
  modern: ["Emerald", "Radiant", "Asscher"],
  vintage: ["Cushion", "Pear", "Marquise"],
  statement: ["Oval", "Pear", "Marquise", "Heart"],
};

const PRICE_BY_BUDGET: Record<Budget, [number, number]> = {
  under5k: [500, 1500],
  "5to10k": [1500, 2500],
  "10to20k": [2500, 3750],
  over20k: [3750, 5000],
  over5k: [5000, 100000],
};

const CARAT_BY_SIZE: Record<Size, [number, number]> = {
  petite: [0.3, 0.9],
  classic: [0.9, 1.6],
  bold: [1.6, 2.5],
  statement: [2.5, 10],
};

function deriveColor(priority: Priority | null): string {
  if (priority === "purity") return "D";
  if (priority === "sparkle") return "E";
  return "";
}

function deriveClarity(priority: Priority | null): string {
  if (priority === "purity") return "VVS1";
  if (priority === "sparkle") return "VVS2";
  if (priority === "balance") return "VS1";
  return "";
}

const STEPS = ["style", "priority", "size", "budget", "shape"] as const;
type StepKey = (typeof STEPS)[number];

function QuizPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({
    style: null,
    priority: null,
    budget: null,
    size: null,
    shape: null,
  });

  const current: StepKey = STEPS[step];
  const total = STEPS.length;

  const update = <K extends keyof Answers>(k: K, v: Answers[K]) => {
    setAnswers((a) => ({ ...a, [k]: v }));
    // auto-advance
    setTimeout(() => {
      setStep((s) => Math.min(s + 1, total));
    }, 180);
  };

  const finish = () => {
    const search: Record<string, string | number> = {};
    if (answers.shape) search.shape = answers.shape;
    const color = deriveColor(answers.priority);
    const clarity = deriveClarity(answers.priority);
    if (color) search.color = color;
    if (clarity) search.clarity = clarity;
    if (answers.budget) {
      const [pmin, pmax] = PRICE_BY_BUDGET[answers.budget];
      search.priceMin = pmin;
      search.priceMax = pmax;
    }
    if (answers.size) {
      const [cmin, cmax] = CARAT_BY_SIZE[answers.size];
      search.caratMin = cmin;
      search.caratMax = cmax;
    }
    navigate({ to: "/shop", search });
  };

  const isResults = step >= total;

  return (
    <div className="mx-auto max-w-3xl px-6 py-20 lg:px-12 lg:py-28">
      <header className="mb-16 text-center">
        <p className="text-xs uppercase tracking-luxe text-muted-foreground">
          {isResults ? "Your Edit" : `Step ${step + 1} of ${total}`}
        </p>
        <h1 className="mt-6 font-serif text-5xl font-light leading-tight lg:text-6xl">
          {isResults ? "Your stone, distilled." : "Find your stone."}
        </h1>
        {!isResults && (
          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            Five questions. We'll narrow the vault to the diamonds most likely to
            be yours.
          </p>
        )}

        {/* progress */}
        <div className="mx-auto mt-10 flex max-w-md gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-px flex-1 transition-colors ${
                i < step ? "bg-foreground" : "bg-hairline"
              }`}
            />
          ))}
        </div>
      </header>

      {!isResults && (
        <div className="min-h-[20rem]">
          {current === "style" && (
            <Question
              label="Which feels most like you?"
              options={[
                { value: "classic", label: "Timeless & Classic", desc: "Quiet confidence." },
                { value: "modern", label: "Clean & Modern", desc: "Architectural lines." },
                { value: "vintage", label: "Romantic & Vintage", desc: "Soft, storied silhouettes." },
                { value: "statement", label: "Bold & Statement", desc: "Made to be seen." },
              ]}
              value={answers.style}
              onChange={(v) => update("style", v as Style)}
            />
          )}
          {current === "priority" && (
            <Question
              label="What matters most?"
              options={[
                { value: "size", label: "Presence", desc: "Size leads the decision." },
                { value: "sparkle", label: "Sparkle", desc: "Maximum brilliance and fire." },
                { value: "purity", label: "Purity", desc: "Colourless, eye-clean, perfect." },
                { value: "balance", label: "Balance", desc: "A considered all-rounder." },
              ]}
              value={answers.priority}
              onChange={(v) => update("priority", v as Priority)}
            />
          )}
          {current === "size" && (
            <Question
              label="How present should the stone feel?"
              options={[
                { value: "petite", label: "Petite", desc: "Under 1ct · subtle, daily." },
                { value: "classic", label: "Classic", desc: "1 – 1.5ct · the standard." },
                { value: "bold", label: "Bold", desc: "1.5 – 2.5ct · a clear presence." },
                { value: "statement", label: "Statement", desc: "2.5ct + · unmistakable." },
              ]}
              value={answers.size}
              onChange={(v) => update("size", v as Size)}
            />
          )}
          {current === "budget" && (
            <Question
              label="A comfortable investment?"
              options={[
                { value: "under5k", label: "$500 – $1,500", desc: "" },
                { value: "5to10k", label: "$1,500 – $2,500", desc: "" },
                { value: "10to20k", label: "$2,500 – $3,750", desc: "" },
                { value: "over20k", label: "$3,750 – $5,000", desc: "" },
                { value: "over5k", label: "$5,000+", desc: "Exceptional stones, no upper limit." },
              ]}
              value={answers.budget}
              onChange={(v) => update("budget", v as Budget)}
            />
          )}
          {current === "shape" && (
            <Question
              label="A shape you're drawn to? (Optional)"
              options={[
                { value: "", label: "Open to suggestions", desc: "Show me everything in the edit." },
                ...(answers.style
                  ? SHAPE_BY_STYLE[answers.style]
                  : SHAPE_OPTIONS.slice(0, 6)
                ).map((s) => ({ value: s, label: s, desc: "" })),
              ]}
              value={answers.shape ?? ""}
              onChange={(v) => update("shape", (v || null) as string | null)}
            />
          )}

          <div className="mt-12 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-luxe text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
            >
              <ArrowLeft className="h-3 w-3" strokeWidth={1.5} />
              Back
            </button>
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(total, s + 1))}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-luxe text-muted-foreground transition-colors hover:text-foreground"
            >
              Skip
              <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {isResults && (
        <div className="border-t border-hairline pt-12">
          <dl className="grid gap-6 sm:grid-cols-2">
            <Summary label="Shape" value={answers.shape ?? "Curated mix"} />
            <Summary
              label="Colour"
              value={
                deriveColor(answers.priority) ||
                COLOR_OPTIONS.join(" · ")
              }
            />
            <Summary
              label="Clarity"
              value={
                deriveClarity(answers.priority) ||
                CLARITY_OPTIONS.slice(0, 3).join(" · ")
              }
            />
            <Summary
              label="Carat"
              value={
                answers.size
                  ? `${CARAT_BY_SIZE[answers.size][0]} – ${CARAT_BY_SIZE[answers.size][1]}ct`
                  : "Any"
              }
            />
            <Summary
              label="Budget"
              value={
                answers.budget
                  ? `$${PRICE_BY_BUDGET[answers.budget][0].toLocaleString()} – $${PRICE_BY_BUDGET[answers.budget][1].toLocaleString()}`
                  : "Open"
              }
            />
          </dl>
          <div className="mt-12 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={finish}
              className="inline-flex items-center gap-3 bg-foreground px-8 py-4 text-xs uppercase tracking-luxe text-background transition-opacity hover:opacity-90"
            >
              View matching stones
              <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => {
                setStep(0);
                setAnswers({
                  style: null,
                  priority: null,
                  budget: null,
                  size: null,
                  shape: null,
                });
              }}
              className="text-xs uppercase tracking-luxe text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Question({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string; desc: string }[];
  value: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <h2 className="mb-8 text-center font-serif text-2xl font-light lg:text-3xl">
        {label}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={o.value || "__any"}
              type="button"
              onClick={() => onChange(o.value)}
              className={`group flex flex-col items-start gap-1 border p-6 text-left transition-colors ${
                selected
                  ? "border-foreground bg-foreground text-background"
                  : "border-hairline hover:border-foreground"
              }`}
            >
              <span className="font-serif text-lg font-light">{o.label}</span>
              {o.desc && (
                <span
                  className={`text-xs ${
                    selected ? "text-background/70" : "text-muted-foreground"
                  }`}
                >
                  {o.desc}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-hairline pb-4">
      <dt className="text-[10px] uppercase tracking-luxe text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-2 font-serif text-xl font-light">{value}</dd>
    </div>
  );
}
